import type { BannerSpendBand } from "@loot-os/api-contract";
import { sql } from "drizzle-orm";
import {
  boolean,
  check,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
} from "drizzle-orm/pg-core";

import { environments } from "./app.js";
import { tenant } from "./auth.js";

// ---- storefront banners ----------------------------------------------------------------------
// Scheduled campaigns of slides on an environment's storefront (`packages/core/src/storefront/
// banners.ts`, docs/storefront.md). At most `BANNER_MAX_LIVE` enabled banners may be live at once;
// every save checks it under a lock on the environment row.

/**
 * One banner: when it runs, who it is shown to, and its slides. Writer: tenant editor
 * (`storefrontBanners.*` in the dashboard). Whole row is safe to return to that tenant's members;
 * the storefront receives only the slides of the banners live for its shopper.
 */
export const storefrontBanners = pgTable(
  "storefront_banners",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    /** Staff-facing label in the dashboard; never shown on the storefront. */
    name: text("name").notNull(),
    /** Off = paused: never served, and not counted against the live limit. */
    enabled: boolean("enabled").notNull().default(false),
    /** null = live as soon as it is enabled. */
    startsAt: timestamp("starts_at", { withTimezone: true }),
    /** null = until paused or deleted. */
    endsAt: timestamp("ends_at", { withTimezone: true }),
    /** `BannerSpendBand`s it is shown to; `{}` = everyone. Read through `parseBannerSpendBands`. */
    spendBands: text("spend_bands").array().$type<BannerSpendBand[]>().notNull().default([]),
    /** Order among the environment's banners, lowest first. */
    position: integer("position").notNull(),
    /** `BannerSlide[]` in order; read only through `parseBannerSlides`. */
    slides: jsonb("slides").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("storefront_banners_env_idx").on(t.environmentId, t.position),
    check(
      "storefront_banners_window",
      sql`${t.startsAt} IS NULL OR ${t.endsAt} IS NULL OR ${t.endsAt} > ${t.startsAt}`,
    ),
  ],
);
